import { colors, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'
import { StageControls } from 'room/common/stage-surface'

/**
 * The knowledge check on the in-person stage: the stage's whole width (the
 * stage pads its sides, so none here), the check's own card shape, and two
 * panes under its header - the content on the left, and on the right a
 * column for the participants with the trainer's buttons at its foot.
 *
 * The height is the stage's less the 20px the word cloud leaves under its
 * card, so the foot of both is on the same line. It is stretched rather than
 * `100%`, or the margin would push the card past the stage.
 */
const Card = styled.div`
	align-self: stretch;
	background-color: ${colors.white};
	border-radius: 42px 18px 18px 18px;
	box-shadow: 0 3px 6px 0 ${colors.black15};
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	min-height: 0;
	overflow: hidden;
	width: 100%;
`

const Header = styled.div`
	flex: none;
`

/**
 * The column is as narrow as its figures and buttons allow: on a small
 * laptop, beside the presenter panel, the question and its answers need the
 * room.
 */
const Panes = styled.div`
	display: grid;
	flex: 1 1 0;
	grid-template-columns: minmax(0, 1fr) clamp(300px, 28%, 420px);
	min-height: 0;
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	min-width: 0;
	overflow-y: auto;
	padding: 32px 40px;

	${ui.responsive.desktopFHD} {
		padding: 40px 56px;
	}
`

const Side = styled.div`
	border-left: 1px solid ${colors.borderLightGray};
	display: flex;
	flex-direction: column;
	min-height: 0;
	min-width: 0;
`

const SidePanel = styled.aside`
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	padding: 32px;

	${ui.responsive.desktopFHD} {
		padding: 40px;
	}
`

/**
 * The trainer's buttons at the foot of the column, where the presenter
 * panel keeps its own: the content keeps the card's whole height. Gone with
 * its controls on the projector.
 */
const Actions = styled.div`
	display: flex;
	flex: none;
	flex-direction: column;
	gap: 12px;
	padding: 16px 32px 32px;

	${ui.responsive.desktopFHD} {
		padding: 16px 40px 40px;
	}
`

/** Buttons share a row while they fit, and each takes a row of its own when not. */
const ActionRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;

	& > * {
		flex: 1 1 auto;
		min-width: 0;
	}
`

type Props = {
	/** The check's header: the trainer, the title and, in the review, its progress. */
	header: ReactNode
	/** What the participants panel is called for assistive tech. */
	sideLabel: string
	/** The participants panel. */
	side: ReactNode
	/** The quiet way out - ending the check, closing the review - above the main row. */
	quietActions?: ReactNode
	/** The column's last row, the main action at its right. */
	mainActions?: ReactNode
}

const StageCard: FC<Props> = ({
	header,
	sideLabel,
	side,
	quietActions,
	mainActions,
	children,
}) => (
	<Card>
		<Header>{header}</Header>
		<Panes>
			<Content>{children}</Content>
			<Side>
				<SidePanel aria-label={sideLabel}>{side}</SidePanel>
				<StageControls>
					{quietActions || mainActions ? (
						<Actions>
							{quietActions ? <ActionRow>{quietActions}</ActionRow> : null}
							{mainActions ? <ActionRow>{mainActions}</ActionRow> : null}
						</Actions>
					) : null}
				</StageControls>
			</Side>
		</Panes>
	</Card>
)

export default StageCard
